/**
 * The "What's new" release notes, newest first. Shown once to each user on sign-in, until read, and
 * again from the header's gift button.
 *
 * A release note is written by hand and groups whatever changes read well together; it need not
 * match a deploy or the dates of the commits in it. `version` is a date, with a suffix for a second
 * note on the same day ("2026-10-05.2"), and is compared as a string: a user has read every note up
 * to the version stored on them (`users.whats_new_seen`, Player_Domain::whats_new_seen), and is shown
 * the ones after it. So a new note needs a version later than every one below, and a published one
 * keeps its version.
 *
 * Each change is { area, kind, text }: `area` is one of AREAS and `kind` one of KINDS.
 */
export const AREAS = {
	food: { label: 'Food', icon: 'restaurant' },
	money: { label: 'Money', icon: 'account_balance_wallet' },
};

export const KINDS = {
	functional: { label: 'New things to do' },
	visual: { label: 'Looks different' },
};

const releases = [
	{
		version: '2026-10-05',
		title: 'The grocery brain',
		intro: 'The grocery list now knows what things are, what they look like, and when we last bought them.',
		changes: [
			{ area: 'food', kind: 'functional', text: 'Tap a grocery item\'s name to open it. Rename it, give it tags and a brand, and paste a product link: the picture comes from the link.' },
			{ area: 'food', kind: 'functional', text: 'Things can be kinds of other things. Trix is a kind of Cereal. A meal idea can ask for "any cereal" or "Trix, specifically", and buying Trix counts as buying cereal.' },
			{ area: 'food', kind: 'functional', text: 'Every ingredient says when we last bought it, so "do we have rice?" has an answer.' },
			{ area: 'food', kind: 'functional', text: 'Search the calendar for past meals. When did we last have tacos? Now you know.' },
			{ area: 'food', kind: 'functional', text: 'Search the meal ideas, and get suggestions from names we already use while typing an ingredient.' },
			{ area: 'food', kind: 'functional', text: 'Add, edit and delete calendar events from a day\'s popup. Birthdays, trips, dentist.' },
			{ area: 'food', kind: 'functional', text: 'A day\'s popup suggests the meals we usually have in that slot.' },
			{ area: 'food', kind: 'functional', text: 'Hover a shopping trip on the calendar to see what was bought. Open an item from it, then the meals it was for, then their ingredients.' },
			{ area: 'food', kind: 'functional', text: 'A Cleanup tab lists meals typed on the calendar that aren\'t meal ideas yet. Make each one an idea, or tell it to leave "Leftovers" alone.' },
			{ area: 'food', kind: 'functional', text: 'A grocery item that was really a meal can be turned into a meal idea from its editor.' },
			{ area: 'food', kind: 'functional', text: 'Works without signal. Check things off in the store basement and they save when the phone finds the internet again. A line at the top says when something is waiting to send.' },
			{ area: 'food', kind: 'visual', text: 'On a phone the calendar is a month grid, with a switch back to the list. Swipe sideways to change month.' },
			{ area: 'food', kind: 'visual', text: 'Swipe between the Food tabs, too.' },
			{ area: 'food', kind: 'visual', text: 'Tags show on the grocery list rows.' },
			{ area: 'food', kind: 'visual', text: 'The shopping history opens from the Groceries heading instead of its own tab.' },
			{ area: 'food', kind: 'visual', text: 'The calendar opens from a copy kept on the device, so it shows up straight away.' },
		],
	},
	{
		version: '2026-10-03',
		title: 'Money, tidied',
		intro: 'New furniture. Same money, sadly.',
		changes: [
			{ area: 'money', kind: 'visual', text: 'A sidebar on the computer and a bar along the bottom on a phone, in place of the old tabs.' },
			{ area: 'money', kind: 'visual', text: 'Budget, Transactions, Accounts, Ledger and Summary all live under Money now.' },
			{ area: 'money', kind: 'visual', text: 'The month is in the header. Tap it to pick another, or a range of months.' },
			{ area: 'money', kind: 'visual', text: 'The Accounts page lists every account on one line, by type, under assets, debts and net worth. A car loan is drawn as a car.' },
			{ area: 'money', kind: 'visual', text: 'Editing a transaction opens a cleaner popup, and search and Add share one line.' },
			{ area: 'money', kind: 'functional', text: 'A transaction that looks like a refund says so.' },
			{ area: 'money', kind: 'functional', text: 'Duplicate transactions get their own Cleanup tab on the transactions page.' },
			{ area: 'money', kind: 'functional', text: 'Undoing a deleted transaction really undoes it now. It used to only pretend.' },
			{ area: 'money', kind: 'functional', text: 'Edit an account\'s name, type, icon and colour, hide its transactions, or merge a duplicate into it after a preview.' },
			{ area: 'money', kind: 'functional', text: 'Select cells in the Ledger to see their total, like the old spreadsheet did.' },
		],
	},
	{
		version: '2026-09-29',
		title: 'Dinner, planned',
		intro: 'The calendar stopped being a chore chart and became a menu.',
		changes: [
			{ area: 'food', kind: 'functional', text: 'Meal Ideas: a list of the meals we make, in groups. Paste a recipe link and it fills in the title, picture and ingredients.' },
			{ area: 'food', kind: 'functional', text: 'Drag a meal idea onto a day.' },
			{ area: 'food', kind: 'functional', text: 'Every day has Breakfast, Lunch, Dinner and Snack, and a note for each.' },
			{ area: 'food', kind: 'functional', text: 'Tap a day to open it: add meals, remove them, or open a meal\'s idea.' },
			{ area: 'food', kind: 'functional', text: 'Put a meal idea\'s ingredients on the grocery list, or a whole group\'s, choosing which ones. Those already on it are marked.' },
			{ area: 'food', kind: 'functional', text: 'One grocery list. Checking something off keeps it on the list, struck through, and the calendar remembers what was bought that day.' },
			{ area: 'food', kind: 'functional', text: 'Old lists became a shopping history you can search.' },
			{ area: 'food', kind: 'visual', text: 'The calendar got a fresh coat of paint, and popups grow out of whatever you tapped.' },
			{ area: 'food', kind: 'visual', text: 'The phone\'s back button closes a popup instead of leaving the page.' },
			{ area: 'food', kind: 'visual', text: 'The Chores tab is gone. Nobody will miss it.' },
		],
	},
];

export default releases;
